<template>
  <div class="ai-parameters-preview" data-cy="ai-parameters-preview">
    <p class="preview-intro">Equipment and operating settings for this recommendation. Review the details here; make changes in the sizing editor after applying.</p>
    <p v-if="option.incomplete" class="completion-notice">
      Incomplete assembly: add the missing parts after Apply or Create. Selected specifications below do not establish verified performance.
    </p>
    <TabView v-model:active-index="activeEquipmentSection" scrollable data-cy="ai-equipment-tabs">
      <TabPanel v-for="(section, index) in sections" :key="section.title" :header="section.title">
        <section v-if="activeEquipmentSection === index" :aria-label="section.title">
          <div class="equipment-cards">
            <article v-for="(item, itemIndex) in detailSections[index]" :key="itemIndex" class="equipment-card">
              <header>
                <span class="equipment-type">{{ item.type }}</span>
                <h3>{{ item.name }}</h3>
                <p v-if="item.partNumber" class="part-number">Part {{ item.partNumber }}</p>
              </header>
              <dl class="equipment-facts">
                <div v-for="value in item.facts" :key="value.label"><dt>{{ value.label }}</dt><dd>{{ value.value }}</dd></div>
              </dl>
              <details v-if="item.housings?.length" class="housing-details">
                <summary>Housing breakdown ({{ item.housings.length }})</summary>
                <div v-for="(housing, housingIndex) in item.housings" :key="housingIndex" class="housing-row">
                  <strong>{{ housing.name }}</strong>
                  <span v-for="value in housing.facts" :key="value.label">{{ value.label }}: {{ value.value }}</span>
                </div>
              </details>
            </article>
            <p v-if="!detailSections[index]?.length" class="empty-selection">No equipment selected for this section.</p>
          </div>
          <ul v-if="sectionErrors(section.placements).length" class="preview-validations" aria-label="Engineering review findings">
            <li v-for="error in sectionErrors(section.placements)" :key="error.name">{{ error.message }}</li>
          </ul>
        </section>
      </TabPanel>
    </TabView>
      <AIDesignCharts v-if="!option.incomplete" :calculations="forms.scenarioCalculations" />
      <p v-else>Complete the assembly and verify it before displaying performance curves.</p>
      <slot name="assessment" />
    <TabView v-model:active-index="activeSection" scrollable>
      <TabPanel header="Strategy &amp; References">
        <section v-if="activeSection === 0" class="strategy-details" data-cy="ai-design-plan">
          <p v-if="!option.designPlan" role="status">
            No LLM strategy was returned with this recommendation. It may have been
            generated with technical planning disabled or by an earlier version.
            A new run with technical planning enabled is needed to include a strategy.
          </p>
          <template v-if="option.designPlan">
            <h3>LLM-proposed demo strategy</h3>
            <p class="strategy-review-note">Engineering review required</p>
            <p>{{ option.designPlan.rationale }}</p>
            <dl class="strategy-metrics">
              <div><dt>Target production</dt><dd>{{ option.designPlan.targetRate }} STB/D</dd></div>
              <div><dt>Operating frequency</dt><dd>{{ option.designPlan.operatingHz }} Hz</dd></div>
              <div><dt>Motor load target</dt><dd>{{ Number(option.designPlan.motorLoadTarget) * 100 }}%</dd></div>
            </dl>
            <p v-if="option.designPlan.operatingEnvelope">
              Checked upper operating point: {{ (option.designPlan.operatingEnvelope as any).maximumHz }} Hz
              (requested {{ (option.designPlan.operatingEnvelope as any).requestedMaximumHz }} Hz).
              {{ (option.designPlan.operatingEnvelope as any).note }}
            </p>
            <template v-if="(option.designPlan.assumptions as string[])?.length">
              <h4>Assumptions and considerations</h4>
              <ul><li v-for="(assumption, index) in (option.designPlan.assumptions as string[])" :key="index">{{ assumption }}</li></ul>
            </template>
            <template v-if="(option.designPlan.equipmentRuleDiagnostics as any[])?.length">
              <h4>Equipment rule coverage</h4>
              <ul><li v-for="(rule, index) in (option.designPlan.equipmentRuleDiagnostics as any[])" :key="index">
                {{ rule.message }} <small>{{ rule.source }}</small>
              </li></ul>
            </template>
          </template>
          <section v-if="option.technicalEvidence?.length" class="technical-references">
            <h4>Technical references ({{ option.technicalEvidence.length }})</h4>
            <details v-for="source in option.technicalEvidence" :key="source.id">
              <summary>{{ source.documentId }} · Rev {{ source.revision }} · Page {{ source.page }}</summary>
              <pre>{{ source.text }}</pre>
            </details>
          </section>
          <p v-else>No technical references were returned with this recommendation.</p>
        </section>
      </TabPanel>
      <TabPanel v-if="user.is_admin" header="Admin Diagnostics">
        <AIDesignDiagnostics v-if="activeSection === 1" :option="option" />
      </TabPanel>
    </TabView>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref } from "vue";
import AIDesignCharts from "./AIDesignCharts.vue";
import AIDesignDiagnostics from "./AIDesignDiagnostics.vue";
import useUserInfo from "@/composables/auth/useUserInfo";
import { equipmentPreviewDetails } from "@/libs/ai/equipmentPreviewDetails";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import type { Scenario, Sizing } from "@/composables/forms/formTypes";
import type { AIEquipmentSuggestionOption } from "@/libs/api/aiEquipmentSuggestions";
import { applySizingRecommendation } from "@/libs/ai/applySizingRecommendation";
import { useScenarioDetailProvider } from "@/composables/scenarioDetailProvider/scenarioDetailProvider";
import { sizingFormKey, sizingKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { equipmentPreviewKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import useForm from "@/composables/forms/useForm";
import { EquipmentCalculatorSectionKey as Section } from "@/composables/scenarioCalculations/calculationTypes";

const props = defineProps<{ option: AIEquipmentSuggestionOption; scenario: Scenario; sizing: Sizing }>();
const user = useUserInfo();
// Remounted by trace/priority. No references to live editor forms are shared.
const scenario = ref<Scenario>(JSON.parse(JSON.stringify(props.scenario)));
const sizing = ref<Sizing>(JSON.parse(JSON.stringify(props.sizing)));
provide(equipmentPreviewKey, true);
provide(sizingKey, sizing);
provide(sizingFormKey, useForm(sizing));
const forms = useScenarioDetailProvider(scenario, { equipmentPreview: true });
applySizingRecommendation(props.option, {
  equipment: forms.equipmentForm,
  parameters: forms.parametersForm,
  pumps: forms.pumpsForm,
  housings: forms.housingsForm,
  intakes: forms.intakesForm,
  seals: forms.sealsForm,
  cables: forms.cablesForm
});
const activeSection = ref(0);
const activeEquipmentSection = ref(0);
const sections = [
  { title: "Operating Parameters", placements: [Section.OPERATING_PARAMETERS] },
  { title: "Intakes", placements: [Section.INTAKE_CONDITIONS] },
  { title: "Pumps", placements: [Section.PUMP] },
  { title: "Motor", placements: [Section.MOTOR] },
  { title: "Seals / Sensor / MLE", placements: [Section.SEAL, Section.SEAL_MOTOR_OIL, Section.MLE] },
  { title: "Cables", placements: [Section.CABLE] },
  { title: "Surface Drive", placements: [Section.SURFACE_DRIVE] }
];
const detailSections = computed(() => props.option.sizingDraft
  ? equipmentPreviewDetails(props.option.sizingDraft, forms.parametersForm.data.value || {})
  : sections.map(() => []));
const sectionErrors = (placements: Section[]) => forms.scenarioValidator.errors.value.filter(error => placements.includes(error.rulePlacement));
</script>

<style scoped>
.preview-intro, .part-number, .empty-selection { color: var(--text-color-secondary); }
.equipment-cards { display: grid; gap: 1rem; padding: 0.5rem 0; }
.equipment-card { border: 1px solid var(--surface-border); border-radius: 8px; padding: 1.25rem; background: var(--surface-card); }
.equipment-type { color: var(--primary-color); font-size: 0.8rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
.equipment-card h3 { color: var(--text-color); margin: 0.4rem 0; font-size: 1.2rem; overflow-wrap: anywhere; }
.part-number { margin: 0.25rem 0 0; font-size: 0.875rem; }
.equipment-facts { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem 1.5rem; margin: 1.25rem 0 0; }
.equipment-facts dt { font-size: 0.85rem; color: var(--text-color-secondary); }
.equipment-facts dd { margin: 0.3rem 0 0; color: var(--text-color); font-weight: 600; overflow-wrap: anywhere; }
.housing-details { border-top: 1px solid var(--surface-border); margin-top: 1.25rem; padding-top: 1rem; }
.housing-details summary { cursor: pointer; font-weight: 600; }
.housing-details summary:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 4px; }
.housing-row { display: flex; flex-wrap: wrap; gap: 0.5rem 1.5rem; padding: 0.75rem 0; border-bottom: 1px solid var(--surface-border); font-size: 0.9rem; }
.housing-row span { color: var(--text-color-secondary); }
.preview-validations, .completion-notice { color: var(--yellow-700); }
.strategy-details {
  color: var(--text-color);
  padding: 1rem;
  line-height: 1.65;
  overflow-wrap: anywhere;
}
.strategy-details h3, .strategy-details h4 { color: var(--text-color); margin: 0 0 0.75rem; }
.strategy-details h4 { margin-top: 1.5rem; }
.strategy-review-note { color: var(--text-color-secondary); margin-top: 0; }
.strategy-details li { margin-bottom: 0.75rem; }
.strategy-metrics { display: flex; flex-wrap: wrap; gap: 1rem 2rem; margin: 1.5rem 0; }
.strategy-metrics dt { color: var(--text-color-secondary); font-size: 0.875rem; }
.strategy-metrics dd { margin: 0.25rem 0 0; font-weight: 600; }
.technical-references details { border: 1px solid var(--surface-border); border-radius: 6px; padding: 0.875rem 1rem; margin-top: 0.75rem; }
.technical-references summary { cursor: pointer; font-weight: 600; }
.technical-references summary:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 4px; }
.technical-references pre { white-space: pre-wrap; font: inherit; margin: 1rem 0 0; }
</style>
